import { parseReading } from './moisture-determination.js';
import { meanOfDecimals, ratioToDecimal, readMassRow, readingToDecimal, READING_ISSUE_CODE } from './exact-decimal.js';
import { ROW_STATUS } from './asphalt-specific-gravity.js';
/**
 * COARSE AGGREGATE DETERMINATIONS — AASHTO T85 (specific gravity and
 * absorption) and AASHTO T96 (Los Angeles abrasion), decisions D267–D274.
 *
 * Every formula is traced to the laboratory's aggregate report workbooks
 * F03 and F04 (docs/REFERENCE_TEST_AUDIT.md sections 3.8 and 3.12), whose
 * saved results were recomputed independently and agree (PROJECT_DOCUMENTATION
 * 15.57). Nothing else is calculated here: no limit, no specification, no
 * grading rule and no pass/fail.
 *
 *   AASHTO T85-14, sheet `3`, determination columns AC, AM, AW:
 *     Absorption, %       = ((B - A) / A) x 100   (AC19, format 0.00)
 *     Bulk SG (SSD)       = B / (B - C)           (AC20, format 0.000)
 *     Bulk SG (OD)        = A / (B - C)           (AC21, format 0.000)
 *     Apparent SG         = A / (A - C)           (AC22, format 0.000)
 *     Averages                                    (F04 BG19:BG22 `=(AC+AM)/2`)
 *
 *   AASHTO T96-02 (2010), sheet `7`:
 *     Total initial mass  = SUM of the fraction masses   (Y32 `=SUM(Y21:AP31)`, 0.0)
 *     Mass passing No. 12 = total - retained on No. 12  (BJ31 `=Y32-BJ30`,    0.0)
 *     Loss, %             = (passing / total) x 100      (BJ32 `=(BJ31/Y32)*100`, 0)
 *
 * THE T85 AVERAGE (D269). The workbook's average cell divides the first two
 * columns by a fixed 2; the owner's rule is the mean of the determinations
 * actually used. The two agree for two determinations, the only case the
 * workbook exemplifies. Averages are taken of the stored, UNROUNDED values, as
 * the workbook averages its unrounded cells.
 *
 * A reading is never corrected. An incomplete or impossible entry is reported
 * with its exact field and a stable code, and is not calculated.
 */
/** Decimal places a stored ratio or percentage is carried to. */
export const COARSE_AGGREGATE_SCALE = 10;
/** DISPLAY precision, from the workbooks' number formats — display only. */
export const COARSE_AGGREGATE_DISPLAY_PLACES = Object.freeze({
    /** T85 AC19:AW19 and BG19 `0.00`. */
    absorption: 2,
    /** T85 AC20:AW22 and BG20:BG22 `0.000`. */
    specificGravity: 3,
    /** T96 Y32 and BJ31 `0.0`. */
    mass: 1,
    /** T96 BJ32 `0`. */
    abrasionLoss: 0,
});
export const COARSE_AGGREGATE_ISSUE_CODE = Object.freeze({
    /** T85: B - C, the denominator of both bulk specific gravities, would be zero or negative. */
    VOLUME_NOT_POSITIVE: 'volume_not_positive',
    /** T85: A - C, the denominator of the apparent specific gravity, would be zero or negative. */
    APPARENT_VOLUME_NOT_POSITIVE: 'apparent_volume_not_positive',
    /** T96: no fraction mass entered. */
    GRADING_REQUIRED: 'grading_required',
    /** T96: the fraction masses add up to zero. */
    TOTAL_MASS_NOT_POSITIVE: 'total_mass_not_positive',
    /** T96: more retained on No. 12 than was put in the machine. */
    RETAINED_EXCEEDS_TOTAL: 'retained_exceeds_total',
});
/** Codes that say something is not entered yet — incomplete, not wrong. */
const NOT_YET_ENTERED = new Set([
    READING_ISSUE_CODE.READING_REQUIRED,
    COARSE_AGGREGATE_ISSUE_CODE.GRADING_REQUIRED,
]);
function settle(issues, missing, calculate) {
    if (issues.some((issue) => !NOT_YET_ENTERED.has(issue.code))) {
        return { status: ROW_STATUS.INVALID, issues, calculation: null };
    }
    const calculation = missing ? null : calculate();
    if (calculation === null)
        return { status: ROW_STATUS.INCOMPLETE, issues, calculation: null };
    return { status: ROW_STATUS.COMPLETE, issues: [], calculation };
}
// ---------------------------------------------------------------------------
// AASHTO T85 — one determination
// ---------------------------------------------------------------------------
/** The three readings, in the order the workbook lists them (rows 14–16). */
export const T85_READING_FIELDS = Object.freeze(['dryMassInAir', 'ssdMassInAir', 'massInWater']);
export function evaluateT85Determination(readings) {
    const { values, issues, missing } = readMassRow(T85_READING_FIELDS, readings);
    const all = [...issues];
    const a = values.get('dryMassInAir');
    const b = values.get('ssdMassInAir');
    const c = values.get('massInWater');
    // Each denominator is named as soon as both of its readings exist.
    if (b !== undefined && c !== undefined && b - c <= 0n) {
        all.push({ field: 'massInWater', code: COARSE_AGGREGATE_ISSUE_CODE.VOLUME_NOT_POSITIVE });
    }
    // A - C above zero also keeps A, the absorption's denominator, above zero:
    // no mass is negative.
    if (a !== undefined && c !== undefined && a - c <= 0n) {
        all.push({ field: 'dryMassInAir', code: COARSE_AGGREGATE_ISSUE_CODE.APPARENT_VOLUME_NOT_POSITIVE });
    }
    return settle(all, missing, () => {
        if (a === undefined || b === undefined || c === undefined)
            return null;
        // Every reading shares READING_SCALE places, which cancel in each ratio.
        return {
            absorptionPercent: ratioToDecimal((b - a) * 100n, a, COARSE_AGGREGATE_SCALE),
            bulkSpecificGravitySsd: ratioToDecimal(b, b - c, COARSE_AGGREGATE_SCALE),
            bulkSpecificGravityOd: ratioToDecimal(a, b - c, COARSE_AGGREGATE_SCALE),
            apparentSpecificGravity: ratioToDecimal(a, a - c, COARSE_AGGREGATE_SCALE),
        };
    });
}
/** Each value's average over the CALCULATED determinations (D269); null when none is. */
export function averageT85(calculations) {
    const used = calculations.filter((row) => Boolean(row));
    const mean = (key) => meanOfDecimals(used.map((row) => row[key]), COARSE_AGGREGATE_SCALE);
    return {
        averageAbsorptionPercent: mean('absorptionPercent'),
        averageBulkSpecificGravitySsd: mean('bulkSpecificGravitySsd'),
        averageBulkSpecificGravityOd: mean('bulkSpecificGravityOd'),
        averageApparentSpecificGravity: mean('apparentSpecificGravity'),
    };
}
// ---------------------------------------------------------------------------
// AASHTO T96 — the grading table and the loss
// ---------------------------------------------------------------------------
/**
 * The size fractions of the workbook's grading table, rows 22–31, in its
 * order: each passes one sieve and is retained on the next. The designations
 * are the workbook's own text, printed as they appear there in both
 * languages. Which fractions a grading uses is the technician's to enter; the
 * workbook does not tie them to the grading designation (D271).
 */
export const T96_GRADING_FRACTIONS = Object.freeze([
    { key: 'f75_63', passing: '75.0 mm (3")', retained: '63.0 mm (2 1/2")' },
    { key: 'f63_50', passing: '63.0 mm (2 1/2")', retained: '50.0 mm (2")' },
    { key: 'f50_37', passing: '50.0 mm (2")', retained: '37.5 mm (1 1/2")' },
    { key: 'f37_25', passing: '37.5 mm (1 1/2")', retained: '25.0 mm (1")' },
    { key: 'f25_19', passing: '25.0 mm (1")', retained: '19.0 mm (3/4")' },
    { key: 'f19_12', passing: '19.0 mm (3/4")', retained: '12.5 mm (1/2")' },
    { key: 'f12_9', passing: '12.5 mm (1/2")', retained: '9.5 mm (3/8")' },
    { key: 'f9_6', passing: '9.5 mm (3/8")', retained: '6.3 mm (1/4")' },
    { key: 'f6_4', passing: '6.3 mm (1/4")', retained: '4.75 mm (No. 4)' },
    { key: 'f4_2', passing: '4.75 mm (No. 4)', retained: '2.36 mm (No. 8)' },
]);
export const T96_FRACTION_KEYS = Object.freeze(T96_GRADING_FRACTIONS.map((fraction) => fraction.key));
export function evaluateT96(readings) {
    const issues = [];
    let total = 0n;
    let entered = 0;
    for (const key of T96_FRACTION_KEYS) {
        // A blank fraction is not part of this grading — it is not a zero reading.
        const parsed = parseReading(readings.grading[key]);
        if (parsed.kind === 'missing')
            continue;
        if (parsed.kind === 'invalid') {
            issues.push({ field: `grading.${key}`, code: READING_ISSUE_CODE.NOT_A_DECIMAL });
        }
        else if (parsed.scaled < 0n) {
            issues.push({ field: `grading.${key}`, code: READING_ISSUE_CODE.NEGATIVE_MASS });
        }
        else {
            total += parsed.scaled;
            entered += 1;
        }
    }
    const gradingUsable = !issues.length;
    let missing = false;
    if (entered === 0 && gradingUsable) {
        missing = true;
        issues.push({ field: 'grading', code: COARSE_AGGREGATE_ISSUE_CODE.GRADING_REQUIRED });
    }
    else if (gradingUsable && total === 0n) {
        issues.push({ field: 'grading', code: COARSE_AGGREGATE_ISSUE_CODE.TOTAL_MASS_NOT_POSITIVE });
    }
    const retainedRead = readMassRow(['retainedMassNo12'], readings);
    issues.push(...retainedRead.issues);
    missing = missing || retainedRead.missing;
    const retained = retainedRead.values.get('retainedMassNo12');
    if (retained !== undefined && gradingUsable && entered > 0 && retained > total) {
        issues.push({ field: 'retainedMassNo12', code: COARSE_AGGREGATE_ISSUE_CODE.RETAINED_EXCEEDS_TOTAL });
    }
    return settle(issues, missing, () => {
        if (retained === undefined || total <= 0n)
            return null;
        const passing = total - retained;
        return {
            totalInitialMass: readingToDecimal(total),
            passingMassNo12: readingToDecimal(passing),
            // Both masses share READING_SCALE places, which cancel.
            lossPercent: ratioToDecimal(passing * 100n, total, COARSE_AGGREGATE_SCALE),
        };
    });
}
//# sourceMappingURL=coarse-aggregate.js.map